import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useBrand } from '@/branding/useBrand'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { MicrosoftLogo, ChevronRight, LockIcon } from './LoginIcons'

const GOLD = '#b8925a'       // reglas y separadores decorativos
const GOLD_TEXT = '#8a6a38'  // mismo dorado oscurecido: el claro no llega a 4.5:1 sobre blanco
const CREAM = '#faf8f5'      // fondo del panel derecho

/**
 * Acceso editorial: vídeo de producción a la izquierda y tarjeta de acceso a la
 * derecha.
 *
 * Lo estrenó Tailored y hoy lo usan dos marcas, así que todo lo que cambia entre
 * ellas —logo, titular y vídeo— llega por `brand`. Lo que no cambia son el
 * negro, el dorado y el crema: son el diseño, no la marca.
 */
export default function LoginEditorial() {
  const { login, isLoading } = useAuth()
  const brand = useBrand()
  const reducedMotion = useReducedMotion()
  const { t } = useTranslation()

  // El layout sólo se elige con un vídeo detrás; si una marca lo pidiera sin
  // vídeo, se quedaría el póster y la pantalla seguiría en pie.
  const video = brand.loginVideo ?? { webm: '', mp4: '', poster: '/360.png' }

  const words = brand.tagline.split(/\s+/)
  const mid = Math.ceil(words.length / 2)
  const taglineLines = [words.slice(0, mid).join(' '), words.slice(mid).join(' ')].filter(Boolean)

  return (
    <div className="flex h-[100dvh] flex-col md:h-auto md:min-h-screen md:flex-row" style={{ backgroundColor: CREAM }}>
      {/* Panel izquierdo — vídeo de producción con overlay monocromo, editorial */}
      <div className="relative h-[38dvh] min-h-[180px] md:h-auto md:flex-none md:w-[55%] overflow-hidden bg-[#231f20] text-white flex items-center px-10 md:px-16 md:py-0">
        {/* fondo — vídeo en bucle; foto estática si el usuario pide menos movimiento */}
        {reducedMotion ? (
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${video.poster})` }}
          />
        ) : (
          <video
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover"
            poster={video.poster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            {/* webm primero: el navegador coge el primero que sabe reproducir */}
            <source src={video.webm} type="video/webm" />
            <source src={video.mp4} type="video/mp4" />
          </video>
        )}
        {/* overlay monocromo, suave: deja ver el vídeo y sostiene el titular en blanco */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: 'linear-gradient(120deg, rgba(35,31,32,0.55) 0%, rgba(35,31,32,0.35) 45%, rgba(35,31,32,0.15) 100%)' }}
        />
        <div className="relative z-10">
          <h1
            className="font-sans font-light leading-tight uppercase text-2xl md:text-5xl [text-shadow:0_1px_12px_rgba(0,0,0,0.45)]"
            style={{ letterSpacing: '0.15em' }}
          >
            {taglineLines.map((line, i) => (
              <span key={line} className="brand-title-in block" style={{ animationDelay: `${i * 160}ms` }}>
                {line}
              </span>
            ))}
          </h1>
          <div
            aria-hidden
            className="brand-title-in mt-4 h-[2px] w-[72px] md:mt-6 md:w-[92px]"
            style={{ backgroundColor: GOLD, animationDelay: '360ms' }}
          />
        </div>
      </div>

      {/* Panel derecho — tarjeta de acceso sobre crema */}
      <div className="md:w-[45%] flex-1 md:flex-none flex items-center justify-center px-6 py-6 md:px-10 md:py-16">
        <div className="relative w-full max-w-[580px]">
          {/* pico tipo bocadillo: apunta al vídeo, sobra cuando los paneles se apilan */}
          <div
            aria-hidden
            className="hidden md:block absolute left-0 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[3px] bg-white"
          />
          <div className="relative rounded-2xl bg-white px-8 py-8 md:px-14 md:py-12 shadow-[0_24px_60px_-24px_rgba(35,31,32,0.25)]">
            <img
              src={brand.logoLogin}
              alt={brand.name}
              className="brand-title-in mx-auto w-full max-w-[240px] md:max-w-[300px]"
              style={{ animationDelay: '320ms' }}
            />

            <h2
              className="brand-title-in mt-7 text-center text-[32px] leading-none text-[#231f20] md:mt-10 md:text-[44px]"
              style={{ fontFamily: "'Playfair Display', serif", animationDelay: '400ms' }}
            >
              {t('login.title')}
            </h2>

            <div
              aria-hidden
              className="brand-title-in mx-auto mt-4 h-[2px] w-[72px] md:mt-5"
              style={{ backgroundColor: GOLD, animationDelay: '460ms' }}
            />

            <p
              className="brand-title-in mx-auto mt-4 max-w-[380px] text-center text-[14px] leading-relaxed text-brand-muted md:mt-6 md:text-[15px]"
              style={{ animationDelay: '520ms' }}
            >
              {t(brand.loginSubtitleKey ?? 'login.subtitle')}
            </p>

            <button
              onClick={login}
              disabled={isLoading}
              className="brand-title-in mt-6 flex w-full items-center justify-center gap-3 rounded-lg bg-[#231f20] py-3.5 md:mt-9 md:py-4 text-[15px] font-semibold text-white transition hover:bg-black disabled:opacity-50"
              style={{ animationDelay: '600ms' }}
            >
              <MicrosoftLogo />
              <span>{isLoading ? t('login.redirecting') : t('login.continueWithMicrosoft')}</span>
              <ChevronRight />
            </button>

            <div
              className="brand-title-in mt-4 flex items-center justify-center gap-1.5 text-[11px] md:mt-5 md:text-[12px]"
              style={{ color: GOLD_TEXT, animationDelay: '680ms' }}
            >
              <LockIcon />
              <span>{t('login.authorizedOnly')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
